// src/pages/PageView.js
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { getResource } from '../data/resources';
import { Prose } from './shared';

const Wrap = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 32px 24px 64px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #0e8c6a;
  text-decoration: none;
  margin-bottom: 20px;

  &:hover {
    text-decoration: underline;
  }
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 24px 0;
`;

const EmptyState = styled.div`
  color: #64748b;
  font-size: 14px;
  padding: 40px 0;
  text-align: center;
`;

function PageView() {
  const { id } = useParams();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getResource(id)
      .then((doc) => {
        if (cancelled) return;
        setResource(doc);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <Wrap>
        <BackLink to="/">&larr; Back to Dashboard</BackLink>
        <EmptyState>Loading page...</EmptyState>
      </Wrap>
    );
  }

  if (error || !resource || resource.kind !== 'page') {
    return (
      <Wrap>
        <BackLink to="/">&larr; Back to Dashboard</BackLink>
        <EmptyState>This page could not be found.</EmptyState>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <BackLink to="/">&larr; Back to Dashboard</BackLink>
      <PageTitle>{resource.title}</PageTitle>
      <Prose>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
          {resource.markdown || ''}
        </ReactMarkdown>
      </Prose>
    </Wrap>
  );
}

export default PageView;
